/**
 * Platform MCQ Editor — RIGHT_INSP slot.
 *
 * Phase 13 P1 (June 2026)
 *
 * The MCQ editor binds on the `multiple_choice` capability — every
 * scene type the catalog declares with `multiple_choice` (Phase 12
 * `interactive_quiz`, classroom-style content_panel quizzes, video
 * MCQ markers, slide-deck MCQ markers) gets the same editor.
 *
 * The editor itself is a thin chooser/editor for the canonical
 * `question_template` shape. Auto-generates a `template_id` if
 * missing (so the audit suite's `mcq_no_template_id` rule never
 * fires for newly-authored scenes).
 *
 * Reusing one tool across many scene types is the framework's main
 * promise. Each consumer reads `domain_metadata.question_template`
 * the same way; the audit suite enforces shape.
 */
import React, { useMemo } from 'react';

import { DesignerTool } from '../DesignerTool';
import { useDesigner } from '../DesignerContext';
import { requiresCapability } from '../predicates';

function MCQEditor() {
  const { scene, setDomainMetadata } = useDesigner();

  const meta = scene?.domain_metadata || {};
  const tmpl = meta.question_template || {
    template_id: '',
    format:      'multiple_choice',
    prompt:      '',
    choices:     [],
    correct_answer_id: null,
  };

  const choices = tmpl.choices || [];

  // Stamp template_id once if blank — audits expect non-empty.
  const templateId = useMemo(() => {
    if (tmpl.template_id) return tmpl.template_id;
    const id = `qt_${scene?.scene_id}_main`;
    return id;
  }, [tmpl.template_id, scene?.scene_id]);

  if (!scene) return null;

  const setTemplate = (patch) => {
    setDomainMetadata({ question_template: { ...tmpl, ...patch } });
  };

  const updateChoice = (idx, patch) => {
    const next = choices.map((c, i) => (i === idx ? { ...c, ...patch } : c));
    setTemplate({ choices: next });
  };

  const setCorrect = (choiceId) => {
    const next = choices.map((c) => ({
      ...c,
      correct: c.choice_id === choiceId,
    }));
    setTemplate({ choices: next, correct_answer_id: choiceId });
  };

  const addChoice = () => {
    const id = `c_${choices.length + 1}_${Date.now().toString(36)}`;
    setTemplate({
      choices: [...choices, { choice_id: id, text: '', correct: false }],
    });
  };

  const removeChoice = (idx) => {
    const next = choices.filter((_, i) => i !== idx);
    setTemplate({ choices: next });
  };

  return (
    <div className="designer-tool" data-tool-id="platform.mcq_editor">
      <h3 className="designer-tool__heading">Question &amp; Answer Choices</h3>
      <div className="designer-tool__row">
        <label className="designer-tool__label">Question text shown to learner</label>
        <textarea
          className="designer-tool__input designer-tool__input--textarea"
          rows={3}
          value={tmpl.prompt || ''}
          aria-label="Question prompt"
          placeholder="e.g. What defensive coverage is shown?"
          onChange={(e) => setTemplate({ prompt: e.target.value })}
        />
      </div>
      <div className="designer-tool__heading designer-tool__heading--sub">
        Answer choices — select the radio button next to the correct answer
      </div>
      {choices.length === 0 && (
        <span className="designer-tool__empty">No choices yet — click + Add choice below.</span>
      )}
      {choices.map((c, i) => (
        <div
          key={c.choice_id || i}
          className="designer-tool__row designer-tool__row--inline"
          data-choice-id={c.choice_id}
          title={c.correct ? 'Correct answer' : 'Wrong answer'}
        >
          <input
            type="radio"
            name={`mcq-correct-${tmpl.template_id || 'pending'}`}
            checked={Boolean(c.correct)}
            aria-label={`Mark choice ${i + 1} as correct`}
            onChange={() => setCorrect(c.choice_id)}
          />
          <input
            type="text"
            className="designer-tool__input"
            value={c.text || ''}
            aria-label={`Choice ${i + 1} text`}
            style={{ flex: 1 }}
            onChange={(e) => updateChoice(i, { text: e.target.value })}
          />
          <button
            type="button"
            className="designer-tool__button designer-tool__button--icon"
            aria-label={`Remove choice ${i + 1}`}
            onClick={() => removeChoice(i)}
          >×</button>
        </div>
      ))}
      <button
        type="button"
        className="designer-tool__button"
        style={{ marginTop: '0.25rem', alignSelf: 'flex-start' }}
        onClick={addChoice}
      >
        + Add choice
      </button>

      <div className="designer-tool__row" style={{ marginTop: '0.75rem' }}>
        <label className="designer-tool__label">Explanation (shown after answering)</label>
        <textarea
          className="designer-tool__input designer-tool__input--textarea"
          rows={2}
          value={tmpl.explanation || ''}
          aria-label="Answer explanation"
          placeholder="Why this answer is correct — shown to the learner after they answer"
          onChange={(e) => setTemplate({ explanation: e.target.value })}
        />
      </div>

      <div className="designer-tool__row" style={{ marginTop: '0.5rem' }}>
        <label className="designer-tool__label">Max attempts before revealing answer</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="number"
            className="designer-tool__input"
            min="1"
            max="5"
            value={tmpl.max_attempts || 2}
            aria-label="Maximum attempts"
            style={{ width: 60 }}
            onChange={(e) => setTemplate({ max_attempts: parseInt(e.target.value) || 2 })}
          />
          <span style={{ fontSize: '0.7rem', color: 'var(--fg-text-muted)' }}>
            After exhausting attempts, the correct answer and explanation are revealed.
          </span>
        </div>
      </div>

      <div className="designer-tool__row" style={{ marginTop: '0.5rem' }}>
        <label className="designer-tool__label">Template ID (auto-generated)</label>
        <input
          type="text"
          className="designer-tool__input"
          value={tmpl.template_id || ''}
          placeholder={templateId}
          aria-label="Question template id"
          style={{ fontSize: '0.7rem', color: 'var(--fg-text-muted)' }}
          onChange={(e) => setTemplate({ template_id: e.target.value })}
        />
      </div>
    </div>
  );
}

export const mcqEditor = new DesignerTool({
  id:         'platform.mcq_editor',
  label:      'MCQ editor',
  slot:       'RIGHT_INSP',
  category:   'inspector',
  applies_to: requiresCapability('multiple_choice'),
  Component:  MCQEditor,
  weight:     65,
  helpText:   'Author the multiple-choice question template attached to this scene.',
});

export default mcqEditor;
